// Excel parsing / chunk building in a Web Worker, so large files never freeze the page.
// Moved out of Create.jsx so the import can run from BackgroundJobContext and survive navigation.

const workerCode = `
importScripts('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');

// Helper function to check if a row is empty
function isEmptyRow(row) {
  if (!row || row.length === 0) return true;

  // Check if all cells are empty, null, undefined, or contain only whitespace
  return row.every(cell => {
    if (cell === null || cell === undefined) return true;
    if (typeof cell === 'string') return cell.trim() === '';
    return false;
  });
}

self.onmessage = function(e) {
  const { type, data } = e.data;

  if (type === 'PROCESS_EXCEL') {
    try {
      // Process Excel data
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      // Filter out empty rows
      const originalRowCount = allRows.length;
      const cleanedRows = allRows.filter(row => !isEmptyRow(row));
      const cleanedRowCount = cleanedRows.length;

      self.postMessage({
        type: 'EXCEL_PROCESSED',
        data: cleanedRows,
        originalRowCount: originalRowCount,
        cleanedRowCount: cleanedRowCount,
        success: true
      });
    } catch (error) {
      self.postMessage({
        type: 'EXCEL_PROCESSED',
        error: error.message,
        success: false
      });
    }
  }

  if (type === 'CREATE_CHUNK') {
    try {
      const { header, chunkRows } = data;
      const chunkData = [header, ...chunkRows];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(chunkData);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const chunkXlsx = XLSX.write(wb, { type: "array", bookType: "xlsx" });

      const base64Chunk = btoa(
        new Uint8Array(chunkXlsx).reduce((acc, b) => acc + String.fromCharCode(b), "")
      );

      self.postMessage({
        type: 'CHUNK_CREATED',
        data: base64Chunk,
        success: true
      });
    } catch (error) {
      self.postMessage({
        type: 'CHUNK_CREATED',
        error: error.message,
        success: false
      });
    }
  }
};
`;

const createWorker = () => {
  const blob = new Blob([workerCode], { type: "application/javascript" });
  return new Worker(URL.createObjectURL(blob));
};

const runWorker = (message, replyType) =>
  new Promise((resolve, reject) => {
    const worker = createWorker();
    worker.onmessage = (e) => {
      if (e.data.type !== replyType) return;
      worker.terminate();
      if (e.data.success) resolve(e.data);
      else reject(new Error(e.data.error));
    };
    worker.onerror = (error) => {
      worker.terminate();
      reject(error);
    };
    worker.postMessage(message);
  });

// Resolves { data: rows, originalRowCount, cleanedRowCount }
export const processExcelWithWorker = (data) =>
  runWorker({ type: "PROCESS_EXCEL", data }, "EXCEL_PROCESSED");

// Resolves the chunk as a base64 .xlsx string
export const createChunkWithWorker = async (header, chunkRows) =>
  (await runWorker({ type: "CREATE_CHUNK", data: { header, chunkRows } }, "CHUNK_CREATED")).data;
